<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ColorReference, EngineeringProduct, TechnicalPassportYarn } from '../types'
import { yarnRoles } from '../sockPassportStructure'

const props = defineProps<{
  rows: TechnicalPassportYarn[]
  materials: EngineeringProduct[]
  colors: ColorReference[]
}>()

const materialSearch = ref('')
const colorSearch = ref('')
const filteredMaterials = computed(() => {
  const query = materialSearch.value.trim().toLocaleLowerCase()
  if (!query) return props.materials
  return props.materials.filter((item) =>
    [item.name, item.sku, item.internal_sku_code, ...item.tags]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase().includes(query)),
  )
})
const filteredColors = computed(() => {
  const query = colorSearch.value.trim().toLocaleLowerCase()
  if (!query) return props.colors
  return props.colors.filter((item) =>
    [item.name, item.code].some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})

function addRow() {
  props.rows.push({ role: 'main', yarn_type: '', yarn_composition: '', color_name: '', color_code: '', comment: '', sort_order: props.rows.length + 1 })
}

function removeRow(index: number) {
  props.rows.splice(index, 1)
  props.rows.forEach((row, rowIndex) => { row.sort_order = rowIndex + 1 })
}

function selectedMaterialId(row: TechnicalPassportYarn): string {
  const match = props.materials.find((item) => item.name === row.yarn_type || item.sku === row.yarn_type)
  return match ? String(match.id) : ''
}

function selectMaterial(row: TechnicalPassportYarn, event: Event) {
  const id = (event.target as HTMLSelectElement).value
  const material = props.materials.find((item) => String(item.id) === id)
  if (!material) return
  row.yarn_type = material.name
  if (!row.yarn_composition?.trim() && material.tags.length) row.yarn_composition = material.tags.join(', ')
}

function selectedColorId(row: TechnicalPassportYarn): string {
  const match = props.colors.find((item) => item.name === row.color_name || item.code === row.color_code)
  return match ? String(match.id) : ''
}

function selectColor(row: TechnicalPassportYarn, event: Event) {
  const id = (event.target as HTMLSelectElement).value
  const color = props.colors.find((item) => String(item.id) === id)
  if (!color) return
  row.color_name = color.name
  row.color_code = color.code || ''
}

function materialFor(row: TechnicalPassportYarn) {
  return props.materials.find((item) => item.name === row.yarn_type || item.sku === row.yarn_type)
}

function isCssColor(value?: string) {
  return Boolean(value && (/^#[0-9a-f]{3,8}$/i.test(value) || /^(rgb|hsl)a?\(/i.test(value)))
}
</script>

<template>
  <section class="yarn-studio">
    <header class="studio-heading">
      <div>
        <p class="eyebrow">{{ $t('engineering.yarnCatalog.eyebrow') }}</p>
        <h3>{{ $t('engineering.passportStructure.yarnsColors') }}</h3>
        <p>{{ $t('engineering.yarnCatalog.help') }}</p>
      </div>
      <div class="catalog-status">
        <span><strong>{{ materials.length }}</strong>{{ $t('engineering.yarnCatalog.materialsAvailable') }}</span>
        <span><strong>{{ colors.length }}</strong>{{ $t('engineering.yarnCatalog.colorsAvailable') }}</span>
        <button type="button" @click="addRow">+ {{ $t('engineering.passportStructure.addYarn') }}</button>
      </div>
    </header>

    <div class="catalog-toolbar">
      <label>
        <span>{{ $t('engineering.yarnCatalog.findMaterial') }}</span>
        <input v-model="materialSearch" type="search" :placeholder="$t('engineering.yarnCatalog.materialSearch')">
      </label>
      <label>
        <span>{{ $t('engineering.yarnCatalog.findColor') }}</span>
        <input v-model="colorSearch" type="search" :placeholder="$t('engineering.yarnCatalog.colorSearch')">
      </label>
      <p>{{ $t('engineering.yarnCatalog.snapshotNote') }}</p>
    </div>

    <div v-if="!rows.length" class="empty-state">
      <strong>{{ $t('engineering.yarnCatalog.empty') }}</strong>
      <span>{{ $t('engineering.yarnCatalog.emptyHelp') }}</span>
      <button type="button" @click="addRow">{{ $t('engineering.passportStructure.addYarn') }}</button>
    </div>

    <div v-else class="yarn-grid">
      <article v-for="(row, index) in rows" :key="row.id ?? index" class="yarn-card">
        <div class="card-index">
          <span>{{ String(index + 1).padStart(2, '0') }}</span>
          <div>
            <strong>{{ row.yarn_type || $t('engineering.yarnCatalog.unassignedMaterial') }}</strong>
            <small>{{ materialFor(row)?.sku || $t('engineering.yarnCatalog.catalogMaterial') }}</small>
          </div>
          <button type="button" :aria-label="$t('engineering.yarnCatalog.removeAria', { number: index + 1 })" @click="removeRow(index)">×</button>
        </div>

        <div class="card-fields">
          <label>
            <span>{{ $t('engineering.passportStructure.role') }}</span>
            <select v-model="row.role">
              <option v-for="value in yarnRoles" :key="value" :value="value">{{ $t(`engineering.yarnRole.${value}`) }}</option>
            </select>
          </label>
          <label class="wide">
            <span>{{ $t('engineering.yarnCatalog.rawMaterial') }}</span>
            <select :value="selectedMaterialId(row)" @change="selectMaterial(row, $event)">
              <option value="">{{ $t('engineering.yarnCatalog.selectMaterial') }}</option>
              <option v-for="material in filteredMaterials" :key="material.id" :value="material.id">{{ material.name }} · {{ material.sku }}</option>
            </select>
          </label>
          <label class="wide">
            <span>{{ $t('engineering.yarnCatalog.compositionSpecification') }}</span>
            <input v-model="row.yarn_composition" type="text" :placeholder="$t('engineering.yarnCatalog.compositionPlaceholder')">
          </label>
          <label class="color-field">
            <span>{{ $t('engineering.yarnCatalog.catalogColor') }}</span>
            <div>
              <i :class="{ 'swatch--coded': !isCssColor(row.color_code) }" :style="isCssColor(row.color_code) ? { backgroundColor: row.color_code } : undefined"></i>
              <select :value="selectedColorId(row)" @change="selectColor(row, $event)">
                <option value="">{{ $t('engineering.yarnCatalog.selectColor') }}</option>
                <option v-for="color in filteredColors" :key="color.id" :value="color.id">{{ color.name }} · {{ color.code }}</option>
              </select>
            </div>
          </label>
          <label>
            <span>{{ $t('engineering.yarnCatalog.colorCode') }}</span>
            <input v-model="row.color_code" type="text" :placeholder="$t('engineering.yarnCatalog.colorCodePlaceholder')">
          </label>
          <label class="full">
            <span>{{ $t('engineering.passportStructure.comment') }}</span>
            <input v-model="row.comment" type="text" :placeholder="$t('engineering.yarnCatalog.instructionPlaceholder')">
          </label>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.yarn-studio{overflow:hidden;border:1px solid #cbd7e5;background:#fff}.studio-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:14px 16px;border-bottom:1px solid #dce6f0}.studio-heading h3{margin:2px 0 4px;color:#173454}.studio-heading p{margin:0;color:#66778b;font-size:12px}.eyebrow{color:#3568a8!important;font-size:9px!important;font-weight:800;letter-spacing:.08em;text-transform:uppercase}.catalog-status{display:flex;align-items:center;gap:8px}.catalog-status>span{display:grid;min-width:64px;padding:6px 8px;background:#f3f7fb;color:#66778b;font-size:9px;text-align:center;text-transform:uppercase}.catalog-status strong{color:#173f70;font-size:16px}.catalog-status button,.empty-state button{padding:8px 11px;border:0;color:#fff;background:#285b9a;font-weight:700}.catalog-toolbar{display:grid;grid-template-columns:minmax(180px,1fr) minmax(180px,1fr) 1.2fr;gap:10px;align-items:end;padding:10px 14px;background:#f5f8fc}.catalog-toolbar label,.card-fields label{display:grid;gap:4px;color:#53677d;font-size:10px;font-weight:700}.catalog-toolbar input,.card-fields input,.card-fields select{width:100%;min-width:0;padding:7px 8px;border:1px solid #b9c8d8;background:#fff;color:#173454}.catalog-toolbar p{margin:0;color:#728095;font-size:10px;line-height:1.4}.yarn-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;padding:12px;background:#edf3fa}.yarn-card{min-width:0;border:1px solid #cbd7e5;background:#fff}.card-index{display:grid;grid-template-columns:28px 1fr 26px;gap:8px;align-items:center;padding:8px 10px;border-bottom:1px solid #dce6f0}.card-index>span{display:grid;width:28px;height:28px;color:#fff;background:#3568a8;font-size:10px;font-weight:800;place-items:center}.card-index div{display:grid;min-width:0}.card-index strong,.card-index small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.card-index strong{color:#173454;font-size:12px}.card-index small{color:#728095;font-size:9px}.card-index button{border:0;color:#9b3025;background:transparent;font-size:20px}.card-fields{display:grid;grid-template-columns:.65fr 1fr;gap:8px;padding:10px}.card-fields .wide,.card-fields .full{grid-column:1/-1}.color-field>div{display:grid;grid-template-columns:30px 1fr}.color-field i{display:block;border:1px solid #9fb2c7;border-right:0;background:#fff}.color-field .swatch--coded{background:repeating-linear-gradient(45deg,#e4ebf2,#e4ebf2 4px,#fff 4px,#fff 8px)}.empty-state{display:grid;gap:6px;padding:32px;color:#66778b;text-align:center;place-items:center}.empty-state strong{color:#173454}@media(max-width:980px){.yarn-grid{grid-template-columns:1fr}}@media(max-width:700px){.studio-heading{flex-direction:column}.catalog-status{flex-wrap:wrap}.catalog-toolbar{grid-template-columns:1fr}.card-fields{grid-template-columns:1fr}.card-fields .wide,.card-fields .full{grid-column:auto}}
</style>
